import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  setConnected,
  setAuthStatus,
  updateEntity,
  removeEntity,
  updateEntities,
} from '../redux/slices/hassioSlice';

// Do not commit the actual token/host; they come from hassioConfig.js which is gitignored
// hassioConfig.js should export: HASS_TOKEN and HASS_HOST
import { HASS_TOKEN, HASS_HOST } from '../hassioConfig';

const WS_URL = (host) => host.replace('http://', 'ws://').replace('https://', 'wss://') + '/api/websocket';

// Ensure we do not open multiple sockets due to React StrictMode double-mounts in dev
let HAS_STARTED = false;

export default function useHassioConnection() {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const messageIdRef = useRef(1);
  const backoffMsRef = useRef(1000); // start with 1s
  const pendingRequestsRef = useRef({});
  const heartbeatRef = useRef(null);
  const hardShutdownRef = useRef(false);

  // ----- Debug helpers -----
  const maskToken = (token) => {
    if (!token || typeof token !== 'string') return 'missing';
    const head = token.slice(0, 6);
    const tail = token.slice(-4);
    return `${head}...${tail}`;
  };
  const log = (...args) => console.log('[HASSIO]', ...args);
  const warn = (...args) => console.warn('[HASSIO]', ...args);
  const error = (...args) => console.error('[HASSIO]', ...args);

  useEffect(() => {
    let stopped = false;

    if (HAS_STARTED) {
      log('Already started; skipping duplicate initialization (likely StrictMode).');
      return () => { };
    }
    HAS_STARTED = true;

    async function bootstrap() {
      try {
        if (!HASS_TOKEN || !HASS_HOST) {
          error('Missing HASS_TOKEN or HASS_HOST in hassioConfig.js');
          dispatch(setAuthStatus('failed'));
          return;
        }

        dispatch(setAuthStatus('pending'));
        log('Bootstrapping connection', {
          host: HASS_HOST,
          wsUrl: WS_URL(HASS_HOST.replace(/\/$/, '')),
          token: maskToken(HASS_TOKEN),
        });
        await connectWebSocket();
      } catch (err) {
        error('Bootstrap error', err);
        dispatch(setConnected(false));
        dispatch(setAuthStatus('failed'));
        scheduleReconnect();
      }
    }

    function scheduleReconnect() {
      if (stopped) return;
      if (reconnectTimerRef.current) return;
      const delay = Math.min(backoffMsRef.current, 30000);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        warn(`Reconnecting... (delay ${delay}ms)`);
        bootstrap();
        backoffMsRef.current = Math.min(backoffMsRef.current * 2, 30000);
      }, delay);
      warn(`Will attempt reconnect in ${delay}ms`);
    }

    function nextMessageId() {
      messageIdRef.current += 1;
      return messageIdRef.current;
    }

    function subscribeToStateChanges() {
      const ws = wsRef.current;
      if (!ws) return;
      const msg = {
        id: nextMessageId(),
        type: 'subscribe_events',
        event_type: 'state_changed',
      };
      try {
        ws.send(JSON.stringify(msg));
        log('Sent subscribe_events state_changed', { id: msg.id });
      } catch (e) {
        error('Failed to send subscribe_events', e);
      }
    }

    function sendRequest(message) {
      return new Promise((resolve, reject) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          reject(new Error('WebSocket not open'));
          return;
        }
        const id = nextMessageId();
        const withId = { id, ...message };
        pendingRequestsRef.current[id] = { resolve, reject };
        try {
          ws.send(JSON.stringify(withId));
          log('Sent request', withId);
        } catch (e) {
          delete pendingRequestsRef.current[id];
          reject(e);
        }
      });
    }

    function handleEventMessage(data) {
      if (data.type === 'event' && data.event && data.event.event_type === 'state_changed') {
        const newState = data.event.data && data.event.data.new_state;
        const entityId = data.event.data && data.event.data.entity_id;
        if (newState && newState.entity_id) {
          dispatch(updateEntity(newState));
          log('Entity updated', newState.entity_id, newState.state);
        } else if (!newState && entityId) {
          // Entity removed
          dispatch(removeEntity(entityId));
          log('Entity removed', entityId);
        }
      } else if (data.type === 'ping') {
        // Respond to server heartbeat
        try {
          wsRef.current && wsRef.current.send(JSON.stringify({ type: 'pong' }));
          log('Received ping; sent pong');
        } catch (e) {
          error('Failed to send pong', e);
        }
      } else {
        // Unhandled event types are logged for debugging
        log('Unhandled message', data);
      }
    }

    function connectWebSocket() {
      return new Promise((resolve, reject) => {
        const url = WS_URL(HASS_HOST.replace(/\/$/, ''));
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
          log('WebSocket already open/connecting, skipping new connect');
          resolve();
          return;
        }
        log('Connecting websocket', url);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          log('WebSocket opened', { readyState: ws.readyState });
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'auth_required') {
              log('Auth required; sending token');
              try {
                ws.send(JSON.stringify({ type: 'auth', access_token: HASS_TOKEN }));
              } catch (e) {
                error('Failed to send auth message', e);
              }
            } else if (data.type === 'auth_ok') {
              log('Auth OK');
              dispatch(setConnected(true));
              dispatch(setAuthStatus('ok'));
              backoffMsRef.current = 1000;
              subscribeToStateChanges();
              // Load initial states over WS
              sendRequest({ type: 'get_states' })
                .then((states) => {
                  log('Initial states loaded:', Array.isArray(states) ? states.length : 0);
                  dispatch(updateEntities(states));
                })
                .catch((e) => error('Failed to load initial states via WS', e));

              // Start heartbeat ping every 30s to keep the connection alive
              if (!heartbeatRef.current) {
                heartbeatRef.current = setInterval(() => {
                  try {
                    wsRef.current && wsRef.current.send(JSON.stringify({ type: 'ping' }));
                    log('Sent ping');
                  } catch (e) {
                    error('Failed to send ping', e);
                  }
                }, 30000);
              }
              resolve();
            } else if (data.type === 'auth_invalid') {
              error('Auth invalid');
              dispatch(setConnected(false));
              dispatch(setAuthStatus('failed'));
              ws.close();
              reject(new Error('auth_invalid'));
            } else if (data.type === 'result' && typeof data.id === 'number') {
              const pending = pendingRequestsRef.current[data.id];
              if (pending) {
                delete pendingRequestsRef.current[data.id];
                if (data.success) {
                  log('Result received', { id: data.id, success: true });
                  pending.resolve(data.result);
                } else {
                  error('Result error', data.error);
                  pending.reject(new Error(data.error?.message || 'Unknown WS error'));
                }
              }
            } else {
              handleEventMessage(data);
            }
          } catch (err) {
            error('WS message parse error', err);
          }
        };

        ws.onerror = (err) => {
          error('WebSocket error', err);
        };

        ws.onclose = (evt) => {
          warn('WebSocket closed', { code: evt?.code, reason: evt?.reason, wasClean: evt?.wasClean });
          dispatch(setConnected(false));
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }
          if (!stopped) scheduleReconnect();
        };
      });
    }

    bootstrap();

    function handleBeforeUnload() {
      hardShutdownRef.current = true;
      try { wsRef.current && wsRef.current.close(); } catch (_) { }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      stopped = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        if (hardShutdownRef.current) {
          try { wsRef.current.close(); } catch (_) { }
          wsRef.current = null;
        } else {
          // Do not close on component unmount (prevents StrictMode duplicate closes)
          log('Component unmounted; keeping WebSocket alive.');
        }
      }
      backoffMsRef.current = 1000;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);
}


